/**
 * Resolving bundled artwork from data, without letting a missing file break
 * the thing that asked for it.
 *
 * Most `require()`s in this app name a literal path, so a typo is a build
 * error and never reaches a user. These don't: the filename comes from a shop
 * item, which comes from the database, and the two can disagree — a row can
 * name art that was never drawn, or that was removed with the item it
 * belonged to. webpack resolves those at runtime and **throws**, and a throw
 * inside a render takes the whole component with it. That's how nine themes
 * came to be missing from the shop entirely: not rendered without a picture,
 * but not rendered at all, which reads as "this item doesn't exist".
 *
 * So: an item's existence is the shop's business, and its artwork is not.
 * Everything here fails soft.
 */

/**
 * The neutral stand-in, for when an item's own art can't be found.
 *
 * Guarded too, and deliberately: the placeholder is generated
 * (tools/generateThemePlaceholders.mjs) rather than committed by hand, so a
 * fresh checkout that hasn't run the generator would otherwise throw from the
 * very function whose job is to not throw.
 */
export function placeholderImage(size = 256) {
    try {
        return require(`@/assets/images/shop-items/${size}w/placeholder.png`)
    } catch (err) {
        return null
    }
}

/**
 * A shop item's own artwork, or `null` if it has none.
 *
 * Returns null rather than a placeholder so a caller that has something
 * better to draw can draw it — the reward celebration falls back to a gem in
 * the item's rarity colour, which says more than a grey square does.
 *
 * @param {object} item A shop item row.
 * @param {number} [size] 256 or 512, the two widths shop art ships at.
 */
export function shopItemArtwork(item, size = 256) {
    if (!item || !item.game) return null

    // `type` is the fallback filename the shop has always used for items that
    // share one picture across a category.
    const file = item.image || (item.type ? `${item.type}.png` : null)
    if (!file) return null

    try {
        return require(`@/assets/images/shop-items/${size}w/${item.game}-shop/${file}`)
    } catch (err) {
        return null
    }
}

/**
 * A shop item's artwork, guaranteed to resolve to *something* drawable.
 *
 * Use this wherever the alternative is an empty or broken element. Where a
 * richer fallback exists, prefer `shopItemArtwork` and handle the null.
 */
export function shopItemImage(item, size = 256) {
    return shopItemArtwork(item, size) || placeholderImage(size)
}
